import React, { useState, useEffect } from "react";
import { C, font, text } from "../theme";
import { retryAfterText } from "./LoginPage";
import { setFlash, takeFlash } from "../lib/flash";

export default function TwoFactorEnroll({ onEnrolled }) {
  const [data, setData] = useState(null); // { qrDataUrl, backupCodes, manualKey }
  const [saved, setSaved] = useState(false);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [loadErr, setLoadErr] = useState("");
  const [copied, setCopied] = useState(false);
  // Acknowledge the gate the user just passed (UX-81) — e.g. "Password updated."
  const [flash, setFlashMsg] = useState(() => takeFlash());

  useEffect(() => { document.title = "Set up two-factor authentication — SGNIC P2P"; }, []);

  useEffect(() => {
    fetch("/api/auth/2fa/enroll/start", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" })
      .then(async (r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setData)
      .catch(() => setLoadErr("Could not start enrollment. Please reload and try again."));
  }, []);

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(data.backupCodes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable — Download still works */ }
  };

  // The codes must leave the screen somehow (UX-82): hand-transcription was the
  // only option before. Download writes a plain-text file.
  const downloadCodes = () => {
    const blob = new Blob([
      "SGNIC P2P two-factor backup codes\nEach code works once. Keep this file somewhere safe.\n\n" + data.backupCodes.join("\n") + "\n",
    ], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sgnic-p2p-backup-codes.txt";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const confirm = async (e) => {
    e.preventDefault();
    setErr("");
    const res = await fetch("/api/auth/2fa/enroll/confirm", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: code }),
    });
    if (!res.ok) {
      // Mirror LoginPage/TwoFactorVerify: a 429/5xx is not a wrong code.
      if (res.status === 429) { setErr(retryAfterText(res) + " Your code was not checked."); return; }
      if (res.status >= 500) { setErr("Something went wrong on our end. Please try again shortly."); return; }
      const b = await res.json().catch(() => ({}));
      setErr(b.error || "Invalid code");
      return;
    }
    setFlash("Two-factor authentication is set up. You're signed in.");
    onEnrolled();
  };

  return (
    <div style={{ fontFamily: font, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.gray50 }}>
      <form onSubmit={confirm} style={{ background: C.white, padding: 32, borderRadius: 10, border: `1px solid ${C.gray200}`, width: 420, display: "flex", flexDirection: "column", gap: 14 }}>
        <h1 style={{ margin: 0, fontSize: text.h1, color: C.gray1100 }}>Set up two-factor authentication</h1>
        {flash && (
          <div role="status" style={{ background: C.successBg, color: C.success, border: `1px solid ${C.successLight}`, borderRadius: 6, padding: "8px 12px", fontSize: text.sm, fontWeight: 600, display: "flex", gap: 8 }}>
            <span style={{ flex: 1 }}>{flash}</span>
            <button type="button" onClick={() => setFlashMsg(null)} aria-label="Dismiss message" style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontWeight: 700, padding: 0 }}>×</button>
          </div>
        )}
        {loadErr && <div role="alert" style={{ color: C.danger, fontSize: text.md }}>{loadErr}</div>}
        {!data ? <div style={{ color: C.gray500, fontSize: text.md }}>Preparing…</div> : (
          <>
            <div style={{ color: C.gray600, fontSize: text.md }}>1. Scan this QR code with Google Authenticator, Microsoft Authenticator, or Authy.</div>
            <img src={data.qrDataUrl} alt="2FA QR code" style={{ width: 180, height: 180, alignSelf: "center" }} />
            {/* No second camera-capable device, or a desktop authenticator (UX-82):
                the same secret, typable by hand. */}
            {data.manualKey && (
              <div style={{ fontSize: text.sm, color: C.gray600 }}>
                Can't scan? Enter this key manually in your authenticator app:
                <code style={{ display: "block", marginTop: 4, padding: "8px 10px", background: C.gray50, borderRadius: 6, fontSize: text.md, letterSpacing: 1, wordBreak: "break-all", userSelect: "all" }}>{data.manualKey}</code>
              </div>
            )}

            <div style={{ color: C.gray600, fontSize: text.md }}>2. Save these one-time backup codes somewhere safe. Each works once.</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, padding: 12, background: C.gray50, borderRadius: 6, fontFamily: "monospace", fontSize: text.md }}>
              {data.backupCodes.map((c) => <span key={c}>{c}</span>)}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={copyCodes} style={{ padding: "6px 12px", background: C.white, color: C.gray800, border: `1px solid ${C.gray300}`, borderRadius: 6, fontSize: text.sm, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                {copied ? "Copied ✓" : "Copy codes"}
              </button>
              <button type="button" onClick={downloadCodes} style={{ padding: "6px 12px", background: C.white, color: C.gray800, border: `1px solid ${C.gray300}`, borderRadius: 6, fontSize: text.sm, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Download .txt
              </button>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: text.md, color: C.gray700 }}>
              <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
              I have saved my backup codes
            </label>

            <div style={{ color: C.gray600, fontSize: text.md }}>3. Enter the current 6-digit code to finish.</div>
            {err && <div role="alert" style={{ color: C.danger, fontSize: text.md }}>{err}</div>}
            <input aria-label="6-digit code" inputMode="numeric" placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} required
              style={{ padding: "10px 12px", border: `1px solid ${C.gray300}`, borderRadius: 6, letterSpacing: 4, textAlign: "center", fontSize: text.h2 }} />
            <button type="submit" disabled={!saved || code.length < 6}
              style={{ padding: "10px 12px", background: saved && code.length >= 6 ? C.gray1100 : C.gray300, color: C.white, border: "none", borderRadius: 6, cursor: saved && code.length >= 6 ? "pointer" : "not-allowed" }}>
              Enable two-factor
            </button>
          </>
        )}
      </form>
    </div>
  );
}
